import * as React from "react"
import {
  type AgingSummary,
  type StatementRowSummary,
  type StatementRowTitle,
} from "@digits-graphql/frontend/graphql-bearer"
import { type MoneyFlow } from "@digits-graphql/frontend/graphql-public"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import { useReportCurrencyOptions } from "src/frontend/components/OS/Applications/Reports/Report/hooks/useReportDocumentOptions"
import WithComments from "src/frontend/components/OS/Applications/Reports/Report/ReportComments/WithComments"
import { useRowSelectionHandlers } from "src/frontend/components/Shared/Layout/Components/Statements/rowSelectionHandlers"
import {
  RowStyled,
  SectionSummaryTitle,
  sectionTitleColor,
  StickyCell,
  Title,
  TotalCell,
} from "src/frontend/components/Shared/Layout/Components/Statements/shared"
import {
  RowBreakdown,
  RowDeltas,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementRowDeltas"
import { type RowDetails } from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"
import { useStatementCellEvents } from "src/frontend/components/Shared/Layout/Components/Statements/useStatementCellEvents"
import { DetailsPopOver } from "src/frontend/components/Shared/Layout/PopOver/DetailsPopOver/DetailsPopOver"

/*
  INTERFACES
*/

export interface SectionTitleProps {
  id: string
  depth: number
  title: StatementRowTitle
  details?: RowDetails
}

export interface TitleProps {
  id: string
  depth: number
  title: StatementRowTitle
  details?: RowDetails
}

export interface SummaryProps {
  id: string
  depth: number
  summary: StatementRowSummary
  details?: RowDetails
}

export interface SectionSummaryProps {
  id: string
  depth: number
  summary: StatementRowSummary
  details?: RowDetails
}

export interface CondensedAgingSummaryProps {
  id: string
  depth: number
  summary: AgingSummary
  details?: RowDetails
}

export const BORDER_STYLE = css`
  border-top: 1px solid transparent;
  padding-top: 3px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 15px;
    right: 0;
    height: 1px;
    z-index: 2;

    background: ${colors.translucentSecondary10};
    @media print {
      & {
        background: ${colors.theme.light.border};
      }
    }
  }

  .details-popover:hover &,
  .popover-active & {
    border-top-color: transparent;
  }
`

export const Amount: React.FC<{
  rowId: string
  moneyFlow: MoneyFlow
  zeroValueText?: string
}> = ({ rowId, moneyFlow, zeroValueText }) => {
  const options = useReportCurrencyOptions()
  const { onMouseEnter, onClick } = useStatementCellEvents(rowId)
  return (
    <TotalCell data-context-id={rowId} onMouseEnter={onMouseEnter} onClick={onClick}>
      {zeroValueText !== undefined && !moneyFlow.value.amount
        ? zeroValueText
        : moneyFlowHelper.currency(moneyFlow, options)}
    </TotalCell>
  )
}

export const Row: React.FC<{
  rowId: string
  className?: string
  depth: number
  details?: RowDetails
  onClick?: () => void
  children?: React.ReactNode
}> = ({ rowId, className, depth, details, onClick, children }) => {
  const { onMouseMove, onMouseLeave } = useRowSelectionHandlers()

  return (
    <RowStyled
      className={className}
      depth={depth}
      onClick={onClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {children}
      {details && <RowBreakdown rowId={rowId} details={details} />}
      {details && <RowDeltas rowId={rowId} details={details} />}
    </RowStyled>
  )
}

export const RowWithDetails: React.FC<{
  className?: string
  details?: RowDetails
  id: string
  depth: number
  children?: React.ReactNode
}> = ({ className, id, depth, details, children }) => (
  <DetailsPopOver rowId={id} depth={depth} details={details}>
    <WithComments contextId={id} disabled>
      <Row className={className} rowId={id} depth={depth} details={details}>
        {children}
      </Row>
    </WithComments>
  </DetailsPopOver>
)

export const ParentRow = styled(RowWithDetails)<{ $collapsed?: boolean }>`
  ${({ $collapsed }) =>
    !$collapsed &&
    css`
      font-weight: ${fonts.weight.heavy};
      margin-bottom: 5px;
    `};
`

export const ParentTitle = styled(Title)<{ $collapsed: boolean; disabled?: boolean }>`
  &[disabled] {
    pointer-events: none;
  }
  &:not([disabled]):hover {
    color: ${colors.primary};
  }
`

export const SectionRow = styled(RowWithDetails)<{ $collapsed?: boolean }>`
  margin-bottom: 5px;

  ${({ $collapsed }) =>
    $collapsed
      ? css`
          font-weight: ${fonts.weight.medium};
        `
      : css`
          font-weight: ${fonts.weight.heavy};
        `};
`

export const ParentCategorySpacing = styled(StickyCell)`
  /* Fill the whole parent grid and define an equivalent one for the child. */
  display: grid;
  grid-column: span 3;
  //grid-column: span calc(var(--statement-total-columns) + 1);
`

const CondensedSectionSummaryText = styled.div`
  margin-bottom: 5px;
  text-align: right;
`

const CondensedSectionSummaryContainer = styled.div`
  margin-bottom: 5px;
  ${sectionTitleColor};
  font-weight: ${fonts.weight.medium};
  display: flex;
  grid-column: span 2;
`

const CondensedSectionSummaryTitle = styled(SectionSummaryTitle)`
  text-transform: unset;
`

export const CondensedSectionSummary: React.FC<SectionSummaryProps> = ({
  id,
  depth,
  summary: { label, moneyFlow },
  details,
}) => {
  const options = useReportCurrencyOptions()
  return (
    <RowWithDetails id={id} depth={depth} details={details}>
      <CondensedSectionSummaryContainer>
        <CondensedSectionSummaryTitle>{label}</CondensedSectionSummaryTitle>
      </CondensedSectionSummaryContainer>
      <CondensedSectionSummaryText data-context-id={id}>
        {moneyFlowHelper.currency(moneyFlow, options)}
      </CondensedSectionSummaryText>
    </RowWithDetails>
  )
}
